import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// Number blocks side by side (the same top edge on the grid) draw their
// values at the same size: the smallest any of them fits. Each block still
// fits its own value (useFittedFontSize) and reports it here.

type RowFits = Record<string, Record<string, number>>;

interface NumberRowFontSizeContextType {
  // Absent outside a canvas (e.g. fullscreen): each block keeps its own size
  reportFit?: (fit: {
    rowKey: string;
    cardId: string;
    // Undefined when the block leaves the row (moved or removed)
    fontSize: number | undefined;
  }) => void;
  fits: RowFits;
  // Each block's row as it's shown, where that's not its own layout's (a
  // dashboard or report reflowed for a narrow window)
  shownRowKeys?: Record<string, string>;
}

export const NumberRowFontSizeContext =
  React.createContext<NumberRowFontSizeContextType>({ fits: {} });

// The row a block's top edge puts it in
export const toRowKey = (layout: Pick<CardLayout, 'y'>): string =>
  String(layout.y);

// A block's value size: its own fit, or the smallest in its row
export const useRowFontSize = ({
  cardId,
  rowKey,
  fontSize,
}: {
  cardId: string;
  rowKey: string;
  fontSize: number;
}): number => {
  const {
    reportFit,
    fits,
    shownRowKeys = {},
  } = useContext(NumberRowFontSizeContext);
  const shownRowKey = shownRowKeys[cardId] ?? rowKey;

  useEffect(() => {
    if (!reportFit) {
      return undefined;
    }
    reportFit({ rowKey: shownRowKey, cardId, fontSize });
    return () =>
      reportFit({ rowKey: shownRowKey, cardId, fontSize: undefined });
  }, [cardId, fontSize, reportFit, shownRowKey]);

  const rowFits = Object.values(fits[shownRowKey] ?? {});
  return reportFit ? Math.min(fontSize, ...rowFits) : fontSize;
};

const NumberRowFontSizeProvider = ({
  children,
  shownLayout,
}: {
  children: React.ReactNode;
  // The blocks' layout as shown, when it can differ from their own
  shownLayout?: Pick<CardLayout, 'i' | 'y'>[];
}) => {
  const [fits, setFits] = useState<RowFits>({});
  const shownRowKeys = useMemo(
    () =>
      shownLayout
        ? Object.fromEntries(shownLayout.map((l) => [l.i, toRowKey(l)]))
        : undefined,
    [shownLayout],
  );

  const reportFit = useCallback(
    ({
      rowKey,
      cardId,
      fontSize,
    }: {
      rowKey: string;
      cardId: string;
      fontSize: number | undefined;
    }) =>
      setFits((current) => {
        const row = current[rowKey] ?? {};
        if (row[cardId] === fontSize) {
          return current;
        }
        if (fontSize === undefined) {
          const { [cardId]: left, ...rest } = row;
          return { ...current, [rowKey]: rest };
        }
        return { ...current, [rowKey]: { ...row, [cardId]: fontSize } };
      }),
    [],
  );

  const value = useMemo(
    () => ({ reportFit, fits, shownRowKeys }),
    [fits, reportFit, shownRowKeys],
  );

  return (
    <NumberRowFontSizeContext.Provider value={value}>
      {children}
    </NumberRowFontSizeContext.Provider>
  );
};

const NO_ROWS: NumberRowFontSizeContextType = { fits: {} };

// Inside, blocks keep their own size, e.g. a block shown fullscreen
export const NoNumberRows = ({ children }: { children: React.ReactNode }) => (
  <NumberRowFontSizeContext.Provider value={NO_ROWS}>
    {children}
  </NumberRowFontSizeContext.Provider>
);

export default NumberRowFontSizeProvider;
